/**
 * How a workspace names its branches and writes its commit titles (§5.4) — the
 * conventions a team agreed on, chosen from presets or written as a template.
 *
 * Workspace-only, like the blocklist: a convention belongs to the repositories
 * being worked on, and no application-wide default could be right for every
 * one of them. It travels in the workspace document (§5.25) through
 * `normalizeWorkspaceConfig`, so the account stores what the app reads.
 *
 * A template is text with placeholders — `{type}`, `{scope}`, `{ticket}`,
 * `{description}`, `{user}`, `{emoji}` — and `[ … ]` around a part that is left
 * out when it has no value (`{type}[({scope})][!]: {description}` is
 * Conventional Commits). The same template is three things: the sentence the
 * agent is asked to follow, the pattern a typed name is checked against, and
 * the name rendered when nobody is there to write one (`autoLand`).
 *
 * A check is advice, never a refusal: a name the team's format does not cover
 * (a release branch, a revert) is still a name git takes.
 */

export type GitFormatKind = 'branch' | 'commit';

/** One of the two formats: a preset by id, or `custom` with its own template. */
export interface GitFormat {
  preset: string;
  /** The template, kept only for `custom` — a preset's is read from the catalogue, so it can be improved. */
  template: string;
  /** Free text the agent is also given: allowed types, scopes, anything the template cannot say. */
  guidance: string;
}

export interface GitFormats {
  branch: GitFormat | null;
  commit: GitFormat | null;
}

export interface GitFormatPreset {
  id: string;
  label: string;
  template: string;
  /** What it is and where it comes from, for the picker and the prompt. */
  note: string;
  /** The values `{type}` may take; absent, any lowercase word. */
  types?: readonly string[];
  /** For a commit format: what goes at the end of the body. */
  footer?: string;
}

export const CUSTOM_FORMAT = 'custom';
export const MAX_FORMAT_TEMPLATE = 120;
export const MAX_FORMAT_GUIDANCE = 1_000;

/** Conventional Commits 1.0 and the Angular convention it came from. */
export const COMMIT_TYPES = ['feat', 'fix', 'docs', 'style', 'refactor', 'perf', 'test', 'build', 'ci', 'chore', 'revert'];
/** GitFlow's prefixes and the Conventional Branch ones. */
export const BRANCH_TYPES = ['feature', 'bugfix', 'hotfix', 'release', 'chore', 'docs', 'refactor', 'test'];

export const BRANCH_PRESETS: readonly GitFormatPreset[] = [
  {
    id: 'type-ticket',
    label: 'Type / ticket-description',
    template: '{type}/[{ticket}-]{description}',
    note: 'GitFlow and Conventional Branch prefixes, the ticket key when there is one, then a few words in kebab-case.',
    types: BRANCH_TYPES
  },
  {
    id: 'type',
    label: 'Type / description',
    template: '{type}/{description}',
    note: 'Conventional Branch without a ticket: the kind of work, then a few words in kebab-case.',
    types: BRANCH_TYPES
  },
  {
    id: 'ticket',
    label: 'Ticket-description',
    template: '{ticket}-{description}',
    note: 'The issue key first, as Jira and Linear link a branch to its ticket.'
  },
  {
    id: 'user',
    label: 'User / description',
    template: '{user}/{description}',
    note: 'The author’s handle first, so everyone’s branches sort together.'
  }
];

export const COMMIT_PRESETS: readonly GitFormatPreset[] = [
  {
    id: 'conventional',
    label: 'Conventional Commits',
    template: '{type}[({scope})][!]: {description}',
    note: 'Conventional Commits 1.0: a type, an optional scope, ! when it breaks something, then a lowercase summary.',
    types: COMMIT_TYPES,
    footer:
      'Footers go at the end of the body after a blank line: `BREAKING CHANGE: <what breaks>` when something does, `Refs: <ticket>` when there is a ticket.'
  },
  {
    id: 'conventional-ticket',
    label: 'Conventional Commits + ticket',
    template: '{type}[({scope})][!]: {ticket} {description}',
    note: 'Conventional Commits with the issue key at the head of the summary, as commitlint’s Jira rules ask.',
    types: COMMIT_TYPES,
    footer: 'Footers go at the end of the body after a blank line: `BREAKING CHANGE: <what breaks>` when something does.'
  },
  {
    id: 'ticket-first',
    label: 'Ticket key first',
    template: '{ticket} {description}',
    note: 'The issue key, then the summary — what Jira smart commits and most trackers link on.'
  },
  {
    id: 'gitmoji',
    label: 'Gitmoji',
    template: '{emoji} {description}',
    note: 'One gitmoji for the kind of change (✨ feature, 🐛 fix, ♻️ refactor, 📝 docs, ✅ tests, 🔧 configuration), then the summary.'
  },
  {
    id: 'imperative',
    label: 'Plain imperative (50/72)',
    template: '{description}',
    note: 'A capitalised imperative subject of about 50 characters, no full stop; the body wrapped at 72 columns.'
  }
];

export function presetsFor(kind: GitFormatKind): readonly GitFormatPreset[] {
  return kind === 'branch' ? BRANCH_PRESETS : COMMIT_PRESETS;
}

export function presetOf(kind: GitFormatKind, format: GitFormat | null): GitFormatPreset | null {
  if (!format || format.preset === CUSTOM_FORMAT) return null;
  return presetsFor(kind).find((one) => one.id === format.preset) ?? null;
}

/** The template a format stands for — a preset's own, or the one somebody wrote. */
export function templateOf(kind: GitFormatKind, format: GitFormat | null): string {
  if (!format) return '';
  return format.preset === CUSTOM_FORMAT ? format.template : (presetOf(kind, format)?.template ?? '');
}

/* ── storage ────────────────────────────────────────────────────────────── */

/** Control characters out — a newline too, unless the field is prose. */
function clean(text: string, keepNewlines: boolean): string {
  let out = '';
  for (const ch of text.replace(/\r\n?/g, '\n')) {
    const code = ch.codePointAt(0) ?? 0;
    out += code >= 32 && code !== 127 ? ch : keepNewlines && ch === '\n' ? ch : ' ';
  }
  return out;
}

function line(value: unknown, max: number): string {
  return typeof value === 'string' ? clean(value, false).trim().slice(0, max).trim() : '';
}

function prose(value: unknown, max: number): string {
  return typeof value === 'string' ? clean(value, true).trim().slice(0, max).trim() : '';
}

function normalizeFormat(kind: GitFormatKind, input: unknown): GitFormat | null {
  if (!input || typeof input !== 'object') return null;
  const given = input as Record<string, unknown>;
  const preset = typeof given.preset === 'string' ? given.preset : '';
  const guidance = prose(given.guidance, MAX_FORMAT_GUIDANCE);
  if (preset === CUSTOM_FORMAT) {
    const template = line(given.template, MAX_FORMAT_TEMPLATE);
    return template ? { preset, template, guidance } : null;
  }
  // A preset this version does not know is no format, not a guess at one.
  return presetsFor(kind).some((one) => one.id === preset) ? { preset, template: '', guidance } : null;
}

/** The workspace's field, clamped; `null` when it says nothing (the sparse document's "no answer"). */
export function normalizeGitFormats(input: unknown): GitFormats | null {
  if (!input || typeof input !== 'object') return null;
  const given = input as Record<string, unknown>;
  const branch = normalizeFormat('branch', given.branch);
  const commit = normalizeFormat('commit', given.commit);
  return branch || commit ? { branch, commit } : null;
}

/* ── templates ──────────────────────────────────────────────────────────── */

export const FORMAT_PLACEHOLDERS = ['type', 'scope', 'ticket', 'description', 'user', 'emoji'] as const;
export type FormatPlaceholder = (typeof FORMAT_PLACEHOLDERS)[number];

type Piece = { text: string } | { slot: FormatPlaceholder };
type Part = { optional: boolean; pieces: Piece[] };

const OPEN = '\uE000';
const CLOSE = '\uE001';

/**
 * `a[b{x}]c` → `a`, optional `b{x}`, `c`. A `[` with no `]` is text, and so is
 * `\[` / `\]` — a team writing `[PROJ-123] Summary` means the brackets.
 */
function parseTemplate(template: string): Part[] {
  const parts: Part[] = [];
  let rest = template.replace(/\\\[/g, OPEN).replace(/\\\]/g, CLOSE);
  while (rest) {
    const open = rest.indexOf('[');
    const close = open >= 0 ? rest.indexOf(']', open) : -1;
    if (open < 0 || close < 0) {
      parts.push({ optional: false, pieces: piecesOf(rest) });
      break;
    }
    if (open > 0) parts.push({ optional: false, pieces: piecesOf(rest.slice(0, open)) });
    parts.push({ optional: true, pieces: piecesOf(rest.slice(open + 1, close)) });
    rest = rest.slice(close + 1);
  }
  return parts;
}

function piecesOf(text: string): Piece[] {
  const out: Piece[] = [];
  const re = /\{(\w+)\}/g;
  let at = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    const name = m[1] as FormatPlaceholder;
    if (!FORMAT_PLACEHOLDERS.includes(name)) continue;
    if (m.index > at) out.push({ text: text.slice(at, m.index) });
    out.push({ slot: name });
    at = m.index + m[0].length;
  }
  if (at < text.length) out.push({ text: text.slice(at) });
  return out.map((piece) =>
    'text' in piece ? { text: piece.text.replaceAll(OPEN, '[').replaceAll(CLOSE, ']') } : piece
  );
}

const escape = (text: string): string => text.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');

function slotPattern(kind: GitFormatKind, slot: FormatPlaceholder, types?: readonly string[]): string {
  switch (slot) {
    case 'type':
      return types?.length ? `(?:${types.map(escape).join('|')})` : '[a-z]+';
    case 'scope':
      return kind === 'branch' ? '[a-z0-9]+(?:-[a-z0-9]+)*' : '[\\w$.\\/-]+';
    case 'ticket':
      return '[A-Za-z][A-Za-z0-9]+-\\d+';
    case 'user':
      return '[A-Za-z0-9._-]+';
    case 'emoji':
      return '(?::[a-z0-9_+-]+:|\\p{Extended_Pictographic}[\\uFE0F\\u200D\\p{Extended_Pictographic}]*)';
    case 'description':
      return kind === 'branch' ? '[a-z0-9]+(?:[-_.][a-z0-9]+)*' : '\\S.*';
  }
}

/** The whole-name pattern a template stands for. */
export function formatPattern(kind: GitFormatKind, template: string, types?: readonly string[]): RegExp {
  const body = parseTemplate(template)
    .map((part) => {
      const inner = part.pieces
        .map((piece) => ('slot' in piece ? slotPattern(kind, piece.slot, types) : escape(piece.text)))
        .join('');
      return part.optional ? `(?:${inner})?` : inner;
    })
    .join('');
  return new RegExp(`^${body}$`, 'u');
}

/**
 * The template with its placeholders filled. An optional part whose
 * placeholders are not all given is left out; a required one left empty is
 * dropped with nothing in its place.
 */
export function renderFormat(template: string, values: Partial<Record<FormatPlaceholder, string>>): string {
  return parseTemplate(template)
    .map((part) => {
      // A part with no placeholder (`[!]`) is a mark the writer adds when it applies — never by default.
      if (part.optional && !part.pieces.some((piece) => 'slot' in piece)) return '';
      if (part.optional && part.pieces.some((piece) => 'slot' in piece && !values[piece.slot])) return '';
      return part.pieces.map((piece) => ('slot' in piece ? (values[piece.slot] ?? '') : piece.text)).join('');
    })
    .join('');
}

/** What the picker shows under a format, and the prompt as its example. */
export function formatExample(kind: GitFormatKind, format: GitFormat | null): string {
  const preset = presetOf(kind, format);
  return renderFormat(templateOf(kind, format), {
    type: preset?.types?.[0] ?? (kind === 'branch' ? 'feature' : 'feat'),
    scope: 'auth',
    ticket: 'PROJ-123',
    user: 'jdoe',
    emoji: '✨',
    description: kind === 'branch' ? 'add-github-login' : 'add sign-in with GitHub'
  });
}

/** A few words of a title as a branch's `{description}`: lowercase, kebab-case, ASCII. */
export function slugOf(text: string, max = 48): string {
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, max)
    .replace(/-+$/, '');
}

/* ── checks ─────────────────────────────────────────────────────────────── */

/**
 * Whether a branch name or a commit subject follows the format, as a sentence
 * to show under the field — or `null`. Never a refusal (see the head of this
 * file); an empty value is the field's own business.
 */
export function formatProblem(kind: GitFormatKind, value: string, format: GitFormat | null): string | null {
  const text = value.trim();
  if (!text || !format) return null;
  const template = templateOf(kind, format);
  if (kind === 'commit' && text.length > 72) return 'Over 72 characters — most tools cut a subject there.';
  if (format.preset === 'imperative' && /\.$/.test(text)) return 'A subject ends without a full stop.';
  if (!template) return null;
  const preset = presetOf(kind, format);
  if (formatPattern(kind, template, preset?.types).test(text)) return null;
  return `Not the workspace’s ${kind === 'branch' ? 'branch' : 'commit'} format — e.g. ${formatExample(kind, format)}.`;
}

/* ── the agent ──────────────────────────────────────────────────────────── */

/** What is known about the work when the agent is asked to name it. */
export interface FormatContext {
  /** The ticket the task came from (§5.21), e.g. `PROJ-123`. */
  ticket?: string | null;
  /** The person's handle, for `{user}`. */
  user?: string | null;
}

function oneLine(text: string): string {
  return text.replace(/\s+/g, ' ').replace(/`/g, "'").trim();
}

/**
 * The sentences an agent is given to follow a format — in the review's Fill
 * with Agent and in the session's workspace note. Empty when there is none.
 */
export function formatInstruction(kind: GitFormatKind, format: GitFormat | null, context: FormatContext = {}): string[] {
  const template = templateOf(kind, format);
  if (!format || !template) return [];
  const preset = presetOf(kind, format);
  const what = kind === 'branch' ? 'Branch names' : 'Commit subjects';
  const lines = [
    `${what} follow this workspace's format \`${oneLine(template)}\` — for example \`${formatExample(kind, format)}\`. A part in [ ] is left out when it has no value.`
  ];
  if (preset) lines.push(preset.note);
  const slots = new Set(parseTemplate(template).flatMap((part) => part.pieces.flatMap((p) => ('slot' in p ? [p.slot] : []))));
  if (slots.has('type')) {
    lines.push(
      preset?.types?.length
        ? `{type} is one of ${preset.types.join(', ')}.`
        : '{type} is one lowercase word for the kind of change.'
    );
  }
  if (kind === 'branch' && slots.has('description'))
    lines.push('{description} is a few lowercase words joined by hyphens.');
  if (slots.has('ticket')) {
    lines.push(
      context.ticket
        ? `{ticket} is \`${oneLine(context.ticket)}\`, the ticket this work comes from.`
        : '{ticket} is the issue key the task names; when there is none, leave that part out.'
    );
  }
  if (slots.has('user') && context.user) lines.push(`{user} is \`${oneLine(context.user)}\`.`);
  if (kind === 'commit' && preset?.footer) lines.push(preset.footer);
  if (format.guidance) lines.push(`The team adds: ${oneLine(format.guidance)}`);
  return lines;
}
